/**
 * What the dashboard leads with: one hero figure, then the KPI row.
 *
 * The hero is one number at display size - exactly one per view, which is why the tiles
 * beside it are stat tiles rather than four more hero figures.
 *
 * **The hero counts whatever the health filter is asking about.** Filter to Degraded and
 * it reads "Targets degraded 1 / 6" in the degraded colour, not "Targets healthy 0 / 1".
 * The denominator is always the whole estate in scope, never the filtered subset -
 * "1 of 1" is true of any filter and therefore tells you nothing.
 *
 * The colour follows the same idea with one rule on top: **green has to be earned by
 * x/x**. A slice that is looking for a problem wears that problem's colour as soon as it
 * finds one; a slice that is looking for health is only green when every target in scope
 * is healthy, and otherwise takes the colour of the worst thing out there - because "3"
 * in green next to "/ 6" claims a clean bill of health that is not true.
 *
 * The status is the *worst* in whatever the figure is describing, never an average. A
 * headline reading "healthy" while one host is unreachable is worse than no headline.
 */

import { Activity, AlertCircle, Boxes, Timer } from 'lucide-react'
import { useMemo } from 'react'

import type { Snapshot, Status, TargetResult } from '../api/types'
import { cn } from '../lib/cn'
import { formatCount, formatMs, formatPct, formatRelative } from '../lib/format'
import { STATUS_PRESENTATION, statusText, worstStatus } from '../lib/status'
import { matchesHealth } from './FilterBar'
import type { HealthFilterId } from './FilterBar'
import { StatTile } from './StatTile'
import { StatusPill } from './ui/StatusPill'

interface OverviewProps {
  snapshot: Snapshot
  /** The selected health slice - what the hero figure counts and how it is coloured. */
  health: HealthFilterId
  className?: string
}

/** What the big number is counting, per slice. */
const HERO_LABELS: Record<HealthFilterId, string> = {
  all: 'Targets healthy',
  healthy: 'Targets healthy',
  attention: 'Targets needing attention',
  unhealthy: 'Targets unhealthy',
  degraded: 'Targets degraded',
}

/**
 * The hero figure for one slice: its label, its N of M, and the status that colours it.
 *
 * ``all`` leads with the healthy count, which is the question a dashboard with no filter
 * applied is answering. Everything else counts its own slice.
 *
 * The status returned drives both the number's colour and the pill beside it, so the
 * two can never disagree about what they are describing.
 */
export function heroFigure(health: HealthFilterId, results: TargetResult[]) {
  const slice: HealthFilterId = health === 'all' ? 'healthy' : health
  const matching = results.filter((result) => matchesHealth(result, slice))
  const count = matching.length
  const total = results.length

  let status: Status
  if (slice !== 'healthy' && count > 0) {
    // A slice hunting for a problem has found one: wear its colour.
    status = worstStatus(matching.map((result) => result.status))
  } else if (total > 0 && count === total) {
    // x/x - every target in scope is what the slice was looking for.
    status = 'healthy'
  } else {
    // Nothing in this slice, or not all of them: the honest colour is the estate's
    // worst, which is green only when there is genuinely nothing wrong anywhere.
    status = worstStatus(results.map((result) => result.status))
  }

  return { label: HERO_LABELS[health], count, total, status }
}

export function Overview({ snapshot, health, className }: OverviewProps) {
  const { rollup, results, history } = snapshot
  const hero = useMemo(() => heroFigure(health, results), [health, results])
  const presentation = STATUS_PRESENTATION[hero.status]

  /** Availability across every target's window - the estate's figure, not one host's. */
  const availability = useMemo(() => {
    const values = Object.values(history)
      .map((entry) => entry.available_pct)
      .filter((value): value is number => value !== null)
    if (values.length === 0) return null
    return values.reduce((sum, value) => sum + value, 0) / values.length
  }, [history])

  /**
   * Slowest round trip per poll, across all targets - the estate's worst case over
   * time. Built by walking each target's window and taking the max at each index,
   * which is a fair series because every target is polled in the same cycle.
   */
  const slowestTrend = useMemo(() => {
    const windows = Object.values(history)
      .map((entry) => entry.samples)
      .filter((samples) => samples.length > 0)
    if (windows.length === 0) return []

    const depth = Math.min(60, Math.max(...windows.map((samples) => samples.length)))
    const points: Array<{ t: number; value: number | null }> = []
    for (let offset = depth; offset > 0; offset -= 1) {
      let worst: number | null = null
      let stamp: number | null = null
      for (const samples of windows) {
        const sample = samples[samples.length - offset]
        if (!sample || sample.ms === null) continue
        if (worst === null || sample.ms > worst) worst = sample.ms
        stamp = stamp ?? sample.t
      }
      if (stamp !== null) points.push({ t: stamp, value: worst })
    }
    return points
  }, [history])

  const withoutKey = results.filter((result) => result.view === 'public').length

  return (
    <section className={cn('grid gap-3', className)} aria-label="Overview">
      {/* Hero */}
      <div className="card flex flex-wrap items-center justify-between gap-6 p-5">
        <div className="flex items-center gap-5">
          <div>
            <p className="text-xs text-ink-secondary">{hero.label}</p>
            <p className="flex items-baseline gap-2">
              {/* Proportional figures, system sans, >=48px - the one hero on the page. */}
              <span
                className={cn('text-6xl leading-none font-semibold', statusText(presentation.token))}
              >
                {hero.count}
              </span>
              <span className="text-xl leading-none text-ink-muted">/ {hero.total}</span>
            </p>
          </div>

          <div className="border-l pl-5">
            <StatusPill status={hero.status} size="lg" />
            <p className="mt-1.5 max-w-xs text-xs text-ink-secondary">
              {presentation.description}
            </p>
          </div>
        </div>

        <dl className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
          {Object.entries(rollup.by_status).map(([status, count]) => {
            const entry = STATUS_PRESENTATION[status as keyof typeof STATUS_PRESENTATION]
            if (!entry || !count) return null
            const Icon = entry.icon
            return (
              <div key={status} className="flex items-center gap-1.5">
                <Icon size={14} aria-hidden className={statusText(entry.token)} />
                <dt className="text-ink-secondary">{entry.label}</dt>
                <dd className="tabular font-semibold text-ink">{count}</dd>
              </div>
            )
          })}
          <div className="flex items-center gap-1.5">
            <dt className="text-ink-muted">Last poll</dt>
            <dd className="text-ink-secondary">{formatRelative(snapshot.last_poll_at)}</dd>
          </div>
        </dl>
      </div>

      {/* KPI row */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Checks failing"
          icon={<AlertCircle size={14} />}
          value={formatCount(rollup.services_down)}
          valueClassName={rollup.services_down > 0 ? statusText('critical') : undefined}
          note={`of ${rollup.services_total} check${rollup.services_total === 1 ? '' : 's'} across every target`}
        />

        <StatTile
          label="Availability"
          icon={<Activity size={14} />}
          value={formatPct(availability)}
          note={
            availability === null
              ? 'No history yet'
              : 'Mean share of samples where each target answered'
          }
        />

        <StatTile
          label="Slowest response"
          icon={<Timer size={14} />}
          value={formatMs(rollup.slowest_ms)}
          note="Worst round trip in the latest poll"
          trend={slowestTrend}
          trendLabel="slowest round trip"
        />

        {/*
          The queue tile distinguishes "nothing parked" from "we were not shown the
          queues". Printing 0 for the second would be a claim the data does not support.
        */}
        <StatTile
          label="Parked messages"
          icon={<Boxes size={14} />}
          value={
            rollup.queue_targets === 0 ? (
              <span className="text-xl text-ink-muted">Needs API key</span>
            ) : (
              formatCount(rollup.queue_totals?.parked ?? 0)
            )
          }
          valueClassName={
            (rollup.queue_totals?.parked ?? 0) > 0 ? statusText('warning') : undefined
          }
          note={
            rollup.queue_targets === 0
              ? `${withoutKey} target${withoutKey === 1 ? '' : 's'} returned the public view`
              : `On repair queues across ${rollup.queue_targets} target${rollup.queue_targets === 1 ? '' : 's'} — terminal until someone looks`
          }
        />
      </div>
    </section>
  )
}
